/**
 * Catalogue de matériaux et recettes : DONNÉES (jamais de coefficient codé en dur dans les calculs).
 * Les valeurs par défaut reprennent les pratiques observées dans les classeurs Excel de référence
 * (docs/01 §4) ; une entreprise peut les remplacer par ses propres recettes de même code.
 */

export type MaterialCategory = 'LIANT' | 'GRANULAT' | 'EAU' | 'ACIER' | 'BLOC' | 'PLANCHER' | 'BOIS' | 'QUINCAILLERIE' | 'ETANCHEITE' | 'COUVERTURE' | 'REVETEMENT' | 'PEINTURE' | 'FAUX_PLAFOND' | 'AUTRE';

export interface MaterialDefinition {
  code: string;
  name: string;
  category: MaterialCategory;
  /** Unité de compte et d'achat (sac, m3, barre, U, kg, m2, L). */
  unit: string;
  /** Arrondi d'achat : unité entière supérieure (sacs, barres, blocs). */
  purchaseCeil?: boolean;
  /** Perte propre au matériau ; absente = perte par défaut du projet. */
  lossRate?: string;
  /** Ciment : masse d'un sac (kg). */
  bagKg?: string;
  /** Acier : diamètre (mm) et masse linéique (kg/m, Ø²/162 si absente). */
  diameterMm?: number;
  kgPerM?: string;
  /** Exclu du chiffrage (eau du réseau…). */
  noCost?: boolean;
}

export interface ConcreteRecipe {
  kind: 'CONCRETE';
  code: string;
  name: string;
  cementKgPerM3: string;
  gravelM3PerM3: string;
  sandM3PerM3: string;
  waterLPerM3?: string;
  source?: string;
}

/** Mortier exprimé par m² d'ouvrage (forme des Excel) : ciment en kg/m², sable en L/m². */
export interface MortarRecipe {
  kind: 'MORTAR';
  code: string;
  name: string;
  cementKgPerM2: string;
  sandLPerM2: string;
  source?: string;
}

export interface MasonryUnitRecipe {
  kind: 'MASONRY_UNIT';
  code: string;
  name: string;
  materialCode: string;
  unitsPerM2: string;
  /** Mortier de pose (joints). */
  jointMortar?: string;
  /** Achat des blocs, ou fabrication sur site (ciment et sable par bloc). */
  supplyMode: 'PURCHASE' | 'ON_SITE';
  making?: { cementKgPerUnit: string; sandLPerUnit: string };
  source?: string;
}

export interface SlabSystemRecipe {
  kind: 'SLAB_SYSTEM';
  code: string;
  name: string;
  blockMaterialCode: string;
  blocksPerM2: string;
  /** Poutrelles : mètres linéaires par m² de plancher. */
  joistMlPerM2: string;
  /** Poutrelles coulées sur place (béton + acier) ou préfabriquées (achetées au ml). */
  joistMode: 'CAST' | 'PREFAB';
  joistConcreteM3PerMl?: string;
  joistMaterialCode?: string;
  /** Acier des poutrelles coulées : ml d'acier par ml de poutrelle, par diamètre. */
  joistRebarMlPerMl?: Record<string, string>;
  source?: string;
}

/**
 * Composition d'un ouvrage : quantités de matériaux par unité d'ouvrage (m², m³, ml ou U),
 * éventuellement complétées d'un mortier par m² (pose de carrelage…). Exemple : carrelage =
 * carreaux 1,05 m²/m² + ciment blanc 0,3 kg/m² + mortier de pose.
 */
export interface KitRecipe {
  kind: 'KIT';
  code: string;
  name: string;
  /** Unité de l'ouvrage à laquelle s'appliquent les quantités. */
  per: 'm2' | 'm3' | 'ml' | 'U';
  /** Sortie de l'élément visée (clé) ; par défaut la sortie principale. */
  output?: string;
  materials: Array<{ code: string; quantity: string; note?: string }>;
  /** Mortier (recette MORTAR, par m²) entrant dans l'ouvrage. */
  mortar?: string;
  source?: string;
}

export type Recipe = ConcreteRecipe | MortarRecipe | MasonryUnitRecipe | SlabSystemRecipe | KitRecipe;

const SRC_EXCEL = 'Pratique observée dans les classeurs Excel de référence (ABD 2024, Doumhasnan 2018)';

export const DEFAULT_MATERIALS: readonly MaterialDefinition[] = [
  { code: 'CIMENT', name: 'Ciment CPJ 42,5 (sac de 50 kg)', category: 'LIANT', unit: 'sac', purchaseCeil: true, bagKg: '50' },
  { code: 'SABLE', name: 'Sable', category: 'GRANULAT', unit: 'm3' },
  { code: 'GRAVIER', name: 'Gravier', category: 'GRANULAT', unit: 'm3' },
  { code: 'EAU', name: 'Eau de gâchage', category: 'EAU', unit: 'L', noCost: true },
  ...[6, 8, 10, 12, 14, 16, 20, 25].map(
    (d): MaterialDefinition => ({ code: `HA${d}`, name: `Acier HA ${d} (T${d})`, category: 'ACIER', unit: 'barre', purchaseCeil: true, diameterMm: d }),
  ),
  { code: 'FIL_ATTACHE', name: "Fil d'attache recuit", category: 'QUINCAILLERIE', unit: 'kg' },
  { code: 'AGGLO15', name: 'Agglo creux de 15 (15 × 20 × 40)', category: 'BLOC', unit: 'U', purchaseCeil: true },
  { code: 'AGGLO20', name: 'Agglo de 20 (20 × 20 × 40)', category: 'BLOC', unit: 'U', purchaseCeil: true },
  { code: 'BRIQUE', name: 'Brique de terre cuite', category: 'BLOC', unit: 'U', purchaseCeil: true },
  { code: 'HOURDIS16', name: 'Hourdis de 16 (entrevous)', category: 'PLANCHER', unit: 'U', purchaseCeil: true },
  { code: 'POUTRELLE', name: 'Poutrelle préfabriquée', category: 'PLANCHER', unit: 'ml' },
  { code: 'POLYANE', name: 'Film polyane', category: 'ETANCHEITE', unit: 'm2' },
  { code: 'TOLE_BAC', name: 'Tôle bac aluminium / galva', category: 'COUVERTURE', unit: 'm2' },
  { code: 'PLANCHE', name: 'Planche de coffrage', category: 'BOIS', unit: 'U', purchaseCeil: true },
  { code: 'CHEVRON', name: 'Chevron', category: 'BOIS', unit: 'U', purchaseCeil: true },
  { code: 'LAMBOURDE', name: 'Lambourde', category: 'BOIS', unit: 'U', purchaseCeil: true },
  { code: 'POINTES', name: 'Pointes', category: 'QUINCAILLERIE', unit: 'kg' },
  // Second œuvre et compléments (sans prix fourni : à renseigner par l'entreprise).
  { code: 'AGGLO10', name: 'Agglo creux de 10 (10 × 20 × 40)', category: 'BLOC', unit: 'U', purchaseCeil: true },
  { code: 'AGGLO20P', name: 'Agglo plein de 20 (20 × 20 × 40)', category: 'BLOC', unit: 'U', purchaseCeil: true },
  { code: 'CIMENT_BLANC', name: 'Ciment blanc (joints)', category: 'LIANT', unit: 'kg' },
  { code: 'MOELLON', name: 'Moellons / pierres de hérissonnage', category: 'GRANULAT', unit: 'm3' },
  { code: 'GRAVILLON', name: 'Gravillons de protection', category: 'GRANULAT', unit: 'm3' },
  { code: 'CARREAU_SOL', name: 'Carreaux de sol (carrelage)', category: 'REVETEMENT', unit: 'm2' },
  { code: 'CARREAU_FAIENCE', name: 'Carreaux de faïence murale', category: 'REVETEMENT', unit: 'm2' },
  { code: 'PLINTHE', name: 'Plinthes carrelées', category: 'REVETEMENT', unit: 'ml' },
  { code: 'COLLE_CARRELAGE', name: 'Colle à carrelage (mortier-colle)', category: 'REVETEMENT', unit: 'kg' },
  { code: 'PEINTURE_VINYLE', name: 'Peinture vinylique (murs, plafonds)', category: 'PEINTURE', unit: 'L' },
  { code: 'PEINTURE_GLYCERO', name: 'Peinture glycérophtalique', category: 'PEINTURE', unit: 'L' },
  { code: 'PEINTURE_ANTIROUILLE', name: 'Peinture antirouille', category: 'PEINTURE', unit: 'L' },
  { code: 'PRIMAIRE_BITUME', name: "Primaire d'accrochage bitumineux", category: 'ETANCHEITE', unit: 'kg' },
  { code: 'MEMBRANE_BITUME', name: 'Membrane bitumineuse (feutre / chape souple)', category: 'ETANCHEITE', unit: 'm2' },
  { code: 'EMULSION_BITUME', name: 'Émulsion bitumineuse (badigeon)', category: 'ETANCHEITE', unit: 'kg' },
  { code: 'HYDROFUGE', name: 'Hydrofuge de masse', category: 'ETANCHEITE', unit: 'L' },
  { code: 'GOUTTIERE', name: 'Gouttière', category: 'COUVERTURE', unit: 'ml' },
  { code: 'DESCENTE_EP', name: "Descente d'eaux pluviales (tuyau PVC)", category: 'COUVERTURE', unit: 'ml' },
  { code: 'PLAQUE_FP', name: 'Plaques de faux plafond', category: 'FAUX_PLAFOND', unit: 'm2' },
];

/** Valeurs usuelles de métré : à valider et adapter par l'ingénieur (modifiables dans les recettes). */
const SRC_USUEL = "Valeur usuelle de métré — à valider par l'ingénieur";

export const DEFAULT_RECIPES: readonly Recipe[] = [
  { kind: 'CONCRETE', code: 'BP150', name: 'Béton de propreté dosé à 150 kg/m³', cementKgPerM3: '150', gravelM3PerM3: '0.8', sandM3PerM3: '0.4', waterLPerM3: '175', source: SRC_EXCEL },
  { kind: 'CONCRETE', code: 'B250', name: 'Béton dosé à 250 kg/m³ (semelles, dallage)', cementKgPerM3: '250', gravelM3PerM3: '0.8', sandM3PerM3: '0.4', waterLPerM3: '175', source: SRC_EXCEL },
  { kind: 'CONCRETE', code: 'BA350', name: 'Béton armé dosé à 350 kg/m³', cementKgPerM3: '350', gravelM3PerM3: '0.8', sandM3PerM3: '0.4', waterLPerM3: '175', source: SRC_EXCEL },
  { kind: 'MORTAR', code: 'JOINT', name: 'Mortier de pose (joints)', cementKgPerM2: '12', sandLPerM2: '30', source: SRC_EXCEL },
  { kind: 'MORTAR', code: 'ENDUIT', name: 'Enduit au mortier de ciment', cementKgPerM2: '5', sandLPerM2: '15', source: SRC_EXCEL },
  { kind: 'MORTAR', code: 'CHAPE', name: 'Chape', cementKgPerM2: '8', sandLPerM2: '18', source: SRC_EXCEL },
  { kind: 'MASONRY_UNIT', code: 'AGGLO15', name: 'Maçonnerie en agglos de 15', materialCode: 'AGGLO15', unitsPerM2: '13', jointMortar: 'JOINT', supplyMode: 'PURCHASE', making: { cementKgPerUnit: '2.385', sandLPerUnit: '9.385' }, source: SRC_EXCEL },
  { kind: 'MASONRY_UNIT', code: 'AGGLO20', name: 'Maçonnerie en agglos de 20', materialCode: 'AGGLO20', unitsPerM2: '13', jointMortar: 'JOINT', supplyMode: 'PURCHASE', making: { cementKgPerUnit: '4', sandLPerUnit: '16' }, source: SRC_EXCEL },
  // Maçonnerie : même face que les agglos de 15 et 20 (40 × 20 cm), donc même nombre de blocs au m².
  { kind: 'MASONRY_UNIT', code: 'AGGLO10', name: 'Maçonnerie en agglos de 10 (cloisons)', materialCode: 'AGGLO10', unitsPerM2: '13', jointMortar: 'JOINT', supplyMode: 'PURCHASE', source: SRC_EXCEL + ' (13 U/m² comme les agglos de 15)' },
  { kind: 'MASONRY_UNIT', code: 'AGGLO20P', name: 'Maçonnerie en agglos pleins de 20 (soubassement)', materialCode: 'AGGLO20P', unitsPerM2: '13', jointMortar: 'JOINT', supplyMode: 'PURCHASE', source: SRC_EXCEL + ' (13 U/m² comme les agglos de 20)' },
  // Mortiers : ciment = épaisseur × dosage ; sable ≈ 1 m³ par m³ de mortier.
  { kind: 'MORTAR', code: 'POSE_CARRELAGE', name: 'Mortier de pose de carrelage (3 cm, 300 kg/m³)', cementKgPerM2: '9', sandLPerM2: '30', source: `${SRC_USUEL} : 0,03 m × 300 kg/m³ = 9 kg/m² ; 0,03 m³ de sable` },
  { kind: 'MORTAR', code: 'TYROLIEN', name: 'Enduit tyrolien projeté (finition, 1 cm, 400 kg/m³)', cementKgPerM2: '4', sandLPerM2: '10', source: `${SRC_USUEL} : 0,01 m × 400 kg/m³ = 4 kg/m² ; 0,01 m³ de sable` },
  { kind: 'MORTAR', code: 'HYDROFUGE', name: 'Enduit hydrofuge (2 cm, 400 kg/m³)', cementKgPerM2: '8', sandLPerM2: '20', source: `${SRC_USUEL} : 0,02 m × 400 kg/m³ = 8 kg/m² ; 0,02 m³ de sable` },
  // Compositions d'ouvrages (matériaux par unité d'ouvrage).
  { kind: 'KIT', code: 'CARRELAGE_SOL', name: 'Carrelage de sol scellé', per: 'm2', materials: [{ code: 'CARREAU_SOL', quantity: '1.05', note: '5 % de coupe et de casse' }, { code: 'CIMENT_BLANC', quantity: '0.3', note: 'joints' }], mortar: 'POSE_CARRELAGE', source: SRC_USUEL },
  { kind: 'KIT', code: 'PLINTHES', name: 'Plinthes carrelées', per: 'ml', output: 'plinthe', materials: [{ code: 'PLINTHE', quantity: '1.05', note: '5 % de coupe' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'FAIENCE', name: 'Faïence murale collée', per: 'm2', materials: [{ code: 'CARREAU_FAIENCE', quantity: '1.05', note: '5 % de coupe et de casse' }, { code: 'COLLE_CARRELAGE', quantity: '5', note: '4 à 6 kg/m² selon le support' }, { code: 'CIMENT_BLANC', quantity: '0.3', note: 'joints' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'PEINTURE_VINYLE_2C', name: 'Peinture vinylique, 2 couches', per: 'm2', materials: [{ code: 'PEINTURE_VINYLE', quantity: '0.25', note: '2 couches à 8 m²/L' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'PEINTURE_GLYCERO_2C', name: 'Peinture glycéro, 2 couches', per: 'm2', materials: [{ code: 'PEINTURE_GLYCERO', quantity: '0.25', note: '2 couches à 8 m²/L' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'PEINTURE_METAL', name: 'Antirouille + glycéro 2 couches (menuiseries métalliques)', per: 'm2', materials: [{ code: 'PEINTURE_ANTIROUILLE', quantity: '0.12' }, { code: 'PEINTURE_GLYCERO', quantity: '0.25' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'ETANCHE_BICOUCHE', name: 'Étanchéité bicouche bitumineuse', per: 'm2', materials: [{ code: 'PRIMAIRE_BITUME', quantity: '0.3' }, { code: 'MEMBRANE_BITUME', quantity: '2.3', note: '2 couches + 15 % de recouvrements' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'ETANCHE_RELEVES', name: "Relevés d'étanchéité bicouche", per: 'm2', output: 'surface_releves', materials: [{ code: 'PRIMAIRE_BITUME', quantity: '0.3' }, { code: 'MEMBRANE_BITUME', quantity: '2.3' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'PROTECTION_GRAVILLON', name: 'Protection en gravillons (4 cm)', per: 'm2', materials: [{ code: 'GRAVILLON', quantity: '0.04' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'BADIGEON_BITUME', name: 'Badigeon bitumineux, 2 couches', per: 'm2', materials: [{ code: 'EMULSION_BITUME', quantity: '1', note: '2 couches à 0,5 kg/m²' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'ENDUIT_HYDROFUGE', name: 'Enduit hydrofuge (mortier + hydrofuge)', per: 'm2', materials: [{ code: 'HYDROFUGE', quantity: '0.16', note: '1 L par sac de ciment, selon le produit' }], mortar: 'HYDROFUGE', source: SRC_USUEL },
  { kind: 'KIT', code: 'TYROLIEN_FINITION', name: "Finition tyrolienne (en plus de l'enduit de base)", per: 'm2', materials: [], mortar: 'TYROLIEN', source: SRC_USUEL },
  { kind: 'KIT', code: 'HERISSONNAGE', name: 'Hérissonnage en moellons', per: 'm3', materials: [{ code: 'MOELLON', quantity: '1.2', note: 'foisonnement et calage' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'FAUX_PLAFOND', name: 'Faux plafond en plaques', per: 'm2', materials: [{ code: 'PLAQUE_FP', quantity: '1.05', note: '5 % de coupe' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'GOUTTIERE', name: 'Gouttière', per: 'ml', materials: [{ code: 'GOUTTIERE', quantity: '1.05' }], source: SRC_USUEL },
  { kind: 'KIT', code: 'DESCENTE_EP', name: "Descente d'eaux pluviales", per: 'ml', materials: [{ code: 'DESCENTE_EP', quantity: '1.05' }], source: SRC_USUEL },
  {
    kind: 'SLAB_SYSTEM',
    code: 'HOURDIS_16_5',
    name: 'Plancher hourdis 16 + 5, poutrelles coulées sur place',
    blockMaterialCode: 'HOURDIS16',
    blocksPerM2: '7',
    joistMlPerM2: '1.117',
    joistMode: 'CAST',
    joistConcreteM3PerMl: '0.019',
    joistRebarMlPerMl: { HA12: '1.5', HA10: '1.5', HA8: '1', HA6: '3.5' },
    source: SRC_EXCEL + ' (65,5 ml de poutrelles pour 58,64 m²)',
  },
];

/** Recette de ferraillage d'un type d'élément (trois niveaux de précision). */
export type RebarSpec =
  | { method: 'RATIO_KG_M3'; kgPerM3: string; split: Record<string, string> }
  | {
      method: 'RATIO_ML_ML';
      /** Base de calcul (DSL) : « L * n », « H * n », « S * n »… */
      base: string;
      mlPerUnit: Record<string, string>;
      /** Longueur utile d'une barre (compatibilité Excel : 10,5 m). Défaut : longueur commerciale du projet. */
      usableLength?: string;
    }
  | { method: 'DETAILED'; bars: Array<{ label: string; material: string; count: string; length: string }> };

/** Ressources affectées à un type d'élément. Absentes = valeurs par défaut de la famille. */
export interface TypeResources {
  concrete?: string;
  blinding?: string;
  masonry?: string;
  /** Mortier de l'ouvrage ; « NONE » = aucun (peinture…). */
  mortar?: string;
  slab?: string;
  rebar?: RebarSpec;
  /** Compositions de l'ouvrage (recettes KIT), appliquées à leur sortie. */
  kits?: string[];
}
